window.theme.getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

window.theme.setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

window.theme.getProductCardHTML = async (template, productIds) => {
  return (
    await Promise.all(
      productIds.map(async (id) => {
        try {
          const response = await fetch(`/products/${id}?view=${template}`, {
            headers: { Accept: "text/html" },
          });
          if (!response.ok) return null;

          const text = await response.text();
          return (
            new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector(".product-card")?.outerHTML ?? null
          );
        } catch (error) {
          console.warn(`Failed to fetch product ${id}:`, error);
          return null;
        }
      }),
    )
  ).filter(Boolean);
};

window.theme.getTagalysRecommendations = async ({
  recommendationId,
  productIds,
  count,
  template,
}) =>
  new Promise((resolve, reject) => {
    Tagalys.APIConnector.Recommendations.call({
      params: {
        recommendationId,
        count,
        productIds,
      },
      onSuccess: async ({ title, products }) => {
        const productsHTML = await window.theme.getProductCardHTML(
          template,
          products.map(({ handle }) => handle),
        );

        resolve({ title, products: productsHTML });
      },
      onFailure: reject,
    });
  });

window.theme.addToWatchlist = ({
  productId: empi,
  variantId: epi,
  productUrl: du,
  imageUrl: iu,
  price: pr,
}) => {
  window._swat.addToWatchList(null, {
    epi,
    empi,
    du,
    iu,
    pr,
  });
};

const createToggleStore = (name) => ({
  current: null,
  onClose: {},
  reopenCart: false,

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const openName = urlParams.get(`open_${name}`);

    if (openName) {
      this.current = openName;
    }
  },

  open(name) {
    const current = this.current;
    if (current) this.close();
    if (current === "cart") {
      this.reopenCart = true;
    }

    this.current = name;
  },

  close() {
    if (this.onClose[this.current]) {
      const close = this.onClose[this.current];
      setTimeout(() => {
        close();
      }, 300);
    }

    if (this.reopenCart) {
      this.current = "cart";
      this.reopenCart = false;
      return;
    }

    this.current = null;
  },
});

const createSizeSelectorStore = (methods) => ({
  error: null,
  selected: null,

  select(variant) {
    this.error = null;
    this.selected = variant;
  },

  async addToCart() {
    this.error = null;

    const json = await Alpine.store("cart").add(this.selected.id, false);
    if (json.status) {
      this.error = json.message;
      return;
    }

    Alpine.store("drawer").open("cart");
  },

  ...methods,
});

const initGorgiasChat = () => {
  const callback = () => {
    window.GorgiasChat.on("widget:opened", () =>
      document.body.classList.add("gorgias-chat-open"),
    );

    window.GorgiasChat.on("widget:closed", () => {
      document.body.classList.remove("gorgias-chat-open");
    });
  };

  if (window.GorgiasChat) {
    callback();
  } else {
    window.addEventListener("gorgias-widget-loaded", () => {
      callback();
    });
  }
};

document.addEventListener("alpine:init", () => {
  Tagalys.setConfiguration({
    api: {
      serverUrl: "https://api-r4.tagalys.com",
      apiKey: "tgls_v2_storefront_a610943c9248f30bf95c1321097c998e",
    },
    currency: {
      fractionalDigits: 2,
      forceFractionalDigits: true,
    },
    analyticsStorageConsentProvided: function () {
      // return true/false based on user's consent settings
      return true;
    },
    track: true,
  });

  Tagalys.setPlatformConfiguration({
    platform: "Shopify",
    countryCode: window.theme.settings.country,
    baseCountryCode: "GB",
    useStorefrontAPIForSecondaryMarkets: true,
    waitForStorefrontAPI: true,
    storefrontAPI: {
      accessToken: "8df1d820b782bf0101fe5a7bba5d5687",
      myShopifyDomain: "yapgdx-1m.myshopify.com",
    },
    metafields: {
      products: [],
    },
  });

  initGorgiasChat();

  Alpine.store("drawer", createToggleStore("drawer"));
  Alpine.store("modal", createToggleStore("modal"));

  Alpine.store("image_view", {
    current:
      window.theme.getCookie("image_view") ||
      window.theme.settings.defaultImageView,

    init() {
      const urlParams = new URLSearchParams(window.location.search);
      const imageView = urlParams.get(`image_view`);

      if (imageView && ["product", "model", "plus_size"].includes(imageView)) {
        this.current = imageView;
      }
    },

    set(view) {
      this.current = view;
      window.theme.setCookie("image_view", view, 30);
    },
  });

  Alpine.store(
    "global_size_selector",
    createSizeSelectorStore({
      product: null,

      show(product) {
        if (!product?.variants?.length) return;
        this.product = product;
        Alpine.store("drawer").open("global-size-selector");
      },

      clear() {
        this.loading = false;
        this.error = null;
        this.product = null;
        this.selected = null;
      },
    }),
  );

  Alpine.store(
    "pdp_size_selector",
    createSizeSelectorStore({
      select(variant) {
        this.error = null;
        this.selected = variant;
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?variant=${variant.id}`,
        );
      },

      init() {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get("variant");
        if (!variantId) return;

        const variant = window.theme.product_variants.find(
          (v) => v.id == parseInt(variantId, 10),
        );

        if (variant) this.select(variant);
      },
    }),
  );

  Alpine.store("size_guide", {
    product: null,

    open(product) {
      this.product = product;
      Alpine.store("modal").open("size-guide");
    },
  });

  Alpine.store("cart", {
    loading: false,
    object: null,
    recommendations: null,

    async fetchCart() {
      const response = await fetch(window.theme.routes.cart, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "text/html",
        },
      });

      const json = await response.json();
      this.object = json;
    },

    async updateRecommendations() {
      if (!this.object.items?.length || !window.theme.settings.cart_recommendations_id) {
        this.recommendations = null;
        return;
      }

      try {
        this.recommendations = await window.theme.getTagalysRecommendations({
          recommendationId: window.theme.settings.cart_recommendations_id,
          productIds: this.object.items.map(({ product_id }) => product_id),
          count: 4,
          template: "cart-recommendations",
        });
      } catch (ex) {
        this.recommendations = null;
      }
    },

    async updateDrawer(drawer) {
      await this.fetchCart();
      await this.updateRecommendations();

      const newContent = new DOMParser()
        .parseFromString(drawer, "text/html")
        .querySelector("#cart-drawer");

      if (newContent)
        document.querySelector("#cart-drawer").innerHTML = newContent.innerHTML;
    },

    async add(variantId, openDrawer = true) {
      this.loading = true;

      if (openDrawer) {
        Alpine.store("drawer").open("cart");
      }

      // Include selling plan data for preorder variants
      const form = document.querySelector("#main-pdp-form");
      const sellingPlanInput = form?.querySelector(".prebuy-selling-plan");
      const locationInput = form?.querySelector(".prebuy-location-property");

      const cartData = {
        id: variantId,
        quantity: 1,
        sections: "cart-drawer",
      };

      // Add selling plan if available
      if (
        sellingPlanInput &&
        !sellingPlanInput.disabled &&
        sellingPlanInput.value
      ) {
        cartData.selling_plan = sellingPlanInput.value;
      }

      // Add location property if available
      if (locationInput && !locationInput.disabled && locationInput.value) {
        cartData.properties = { _locationId: locationInput.value };
      }

      const body = JSON.stringify(cartData);
      const response = await fetch(window.theme.routes.cartAdd, {
        method: "POST",
        body,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
      });

      const json = await response.json();
      if (json.sections) {
        await this.updateDrawer(json.sections["cart-drawer"]);
      }

      Alpine.store("modal").close();
      this.loading = false;

      return json;
    },

    async remove(variantId) {
      this.loading = true;

      const body = JSON.stringify({
        updates: {
          [variantId]: 0,
        },
        sections: "cart-drawer",
      });

      const response = await fetch(window.theme.routes.cartUpdate, {
        method: "POST",
        body,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
      });

      const json = await response.json();
      if (json.sections) {
        await this.updateDrawer(json.sections["cart-drawer"]);
      }

      this.loading = false;
      return json;
    },

    async init() {
      await this.fetchCart();
      await this.updateRecommendations();
    },
  });

  Alpine.store("search", {
    visible: false,
    loading: true,
    query: "",
    tagalysSearch: null,
    recommendations: null,
    suggestions: null,

    toggle() {
      this.visible = !this.visible;
    },

    async updateSuggestions() {
      if (!this.query) {
        this.suggestions = null;
        return;
      }

      this.loading = true;
      this.tagalysSearch.helpers.updateQuery(this.query);
    },

    async init() {
      const { products: recommendations } =
        await window.theme.getTagalysRecommendations({
          recommendationId: window.theme.settings.search_recommendations_id,
          count: 4,
          template: "search",
        });

      this.recommendations = recommendations;
      this.tagalysSearch = Tagalys.APIConnector.SearchSuggestions.new({
        params: {
          sections: {
            products: { count: 4 },
          },
        },
        onSuccess: async function ({
          sections: {
            products: { items: products },
          },
        }) {
          if (!products?.length) {
            Alpine.store("search").suggestions = [];
            Alpine.store("search").loading = false;
            return;
          }

          const productsHTML = await window.theme.getProductCardHTML(
            "search",
            products.map(({ handle }) => handle),
          );

          Alpine.store("search").suggestions = productsHTML;
          Alpine.store("search").loading = false;
        },
      });

      this.loading = false;

      Alpine.effect(() => {
        const value = this.query;
        this.updateSuggestions();
      });
    },
  });

  Alpine.store("localization", {
    shopify_localization: window.theme.settings.country,
    user_localization: null,

    async getCountryFromIP() {
      try {
        const response = await fetch("https://ipapi.co/country_code/");
        const countryCode = await response.text();
        return countryCode.trim();
      } catch (error) {
        console.error("Geolocation failed, using default");
        return "GB";
      }
    },

    async init() {
      const url = new URL(window.location.href);
      const countryParam = url.searchParams.get("country");

      if (countryParam) {
        window.theme.setCookie("localization_redirected", countryParam, 7);
        return;
      }

      const savedCountry = window.theme.getCookie("localization_redirected");
      if (savedCountry) {
        return;
      }

      this.user_localization = await this.getCountryFromIP();

      if (this.user_localization !== this.shopify_localization) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("country", this.user_localization);
        window.location.replace(newUrl.toString());
      }
    },
  });
});
